// `useSupportWhatsApp` — TanStack Query wrapper around /support/whatsapp.
// Falls back to the legacy support URL while the backend response is loading.

import { useQuery } from "@/lib/query";
import { fetchSupportWhatsApp, type SupportWhatsAppView } from "./api";

const DEFAULT_FALLBACK_PHONE = "553491329018";
const DEFAULT_FALLBACK_MESSAGE = "Ola, preciso de suporte do QuartelBot";
const DEFAULT_SUPPORT_URL = "https://encurta.ai/suportequartelbot";

export interface SupportWhatsAppLink {
  readonly href: string | null;
}

export function useSupportWhatsApp(): SupportWhatsAppLink {
  const query = useQuery({
    queryKey: ["support", "whatsapp"],
    queryFn: fetchSupportWhatsApp,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return resolveSupportWhatsAppLink(query.data);
}

export function resolveSupportWhatsAppLink(
  view: SupportWhatsAppView | undefined,
): SupportWhatsAppLink {
  if (view === undefined) {
    return { href: readFallbackUrl() };
  }
  if (view.url === null) {
    return { href: null };
  }
  const remoteUrl = view.url.trim();
  return { href: remoteUrl.length > 0 ? remoteUrl : null };
}

function readFallbackUrl(): string | null {
  const configured = import.meta.env["VITE_SUPPORT_WHATSAPP_URL"];
  if (typeof configured === "string" && isHttpUrl(configured)) {
    return configured;
  }
  if (isHttpUrl(DEFAULT_SUPPORT_URL)) {
    return DEFAULT_SUPPORT_URL;
  }
  if (DEFAULT_FALLBACK_PHONE.length === 0) {
    return null;
  }
  return `https://wa.me/${DEFAULT_FALLBACK_PHONE}?text=${encodeURIComponent(DEFAULT_FALLBACK_MESSAGE)}`;
}

function isHttpUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}
